/**
 * Pure helpers so a navigated period (month / year / day) lives in the URL and
 * is copyable/shareable (#15b total URL addressability).
 *
 * Canonical URL formats:
 *   - month → `YYYY-MM`     (e.g. `2025-03`)
 *   - year  → `YYYY`        (e.g. `2025`)
 *   - day   → `YYYY-MM-DD`  (e.g. `2025-03-14`)
 *
 * Idempotent: parse → format → parse returns the same value, and defaults
 * (the "current" month/year/day) are omitted from the URL by the navigation layer.
 */

const MONTH_RE = /^(\d{4})-(\d{2})$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const YEAR_RE = /^(\d{4})$/;

/** `YYYY-MM` for a date (in its local month/year). */
export function monthKeyFromDate(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${mm}`;
}

/** `YYYY-MM-DD` for a date (local day). */
export function dayKeyFromDate(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${mm}-${dd}`;
}

/** `Date` (local midnight on day 1 of the month) from `YYYY-MM`, or `null` if invalid. */
export function dateFromMonthKey(value: unknown): Date | null {
  if (typeof value !== "string") {
    return null;
  }
  const m = MONTH_RE.exec(value);
  if (!m) {
    return null;
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) {
    return null;
  }
  return new Date(year, month - 1, 1);
}

/** `Date` (local midnight) from `YYYY-MM-DD`, or `null` if invalid. */
export function dateFromDayKey(value: unknown): Date | null {
  if (typeof value !== "string") {
    return null;
  }
  const m = DAY_RE.exec(value);
  if (!m) {
    return null;
  }
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const d = new Date(year, month - 1, day);
  // Reject overflow (e.g. 2025-02-31 → March).
  if (d.getMonth() !== month - 1 || d.getDate() !== day) {
    return null;
  }
  return d;
}

/** Year (number) from `YYYY`, or `null` if invalid. */
export function yearFromKey(value: unknown): number | null {
  if (typeof value !== "string") {
    return null;
  }
  const m = YEAR_RE.exec(value);
  return m ? Number(m[1]) : null;
}

/** Valid (normalized) `YYYY-MM`, or `undefined`. */
export function sanitizeMonthKey(value: unknown): string | undefined {
  const d = dateFromMonthKey(value);
  return d ? monthKeyFromDate(d) : undefined;
}

/** Valid (normalized) `YYYY-MM-DD`, or `undefined`. */
export function sanitizeDayKey(value: unknown): string | undefined {
  const d = dateFromDayKey(value);
  return d ? dayKeyFromDate(d) : undefined;
}

/** Valid (normalized) `YYYY`, or `undefined`. */
export function sanitizeYearKey(value: unknown): string | undefined {
  const y = yearFromKey(value);
  return y === null ? undefined : String(y);
}
